import React, { useEffect, useState, useCallback, useRef } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, ChevronLeft, ChevronRight, Download, ZoomIn, ZoomOut, Maximize2 } from "lucide-react";
import HTMLFlipBook from "react-pageflip";
import { LINKS } from "../data/content";

const TOTAL = 17;
const pageSrc = (n) => `/deck/page-${String(n).padStart(2, "0")}.jpg`;
const hiSrc = (n) => `/deck/hi/page-${String(n).padStart(2, "0")}.jpg`;

const Page = React.forwardRef(({ number, hard }, ref) => (
  <div
    ref={ref}
    className="deck-flip-page group relative cursor-zoom-in bg-mds-white"
    data-density={hard ? "hard" : "soft"}
    data-testid={`deck-page-${number}`}
  >
    <img
      src={pageSrc(number)}
      alt={`Sponsorship deck page ${number}`}
      className="h-full w-full object-cover"
      draggable={false}
    />
    <span className="pointer-events-none absolute bottom-3 right-3 flex h-9 w-9 items-center justify-center rounded-full bg-black/40 text-mds-cream opacity-0 backdrop-blur-sm transition-opacity duration-300 group-hover:opacity-100">
      <ZoomIn size={16} />
    </span>
  </div>
));
Page.displayName = "Page";

const ZoomViewer = ({ page, onClose }) => {
  const [scale, setScale] = useState(1);
  const [pos, setPos] = useState({ x: 0, y: 0 });
  const drag = useRef({ active: false, x: 0, y: 0 });
  const moved = useRef(false);

  useEffect(() => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  }, [page]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Escape") onClose();
      if (e.key === "+" || e.key === "=") setScale((s) => Math.min(4, s + 0.5));
      if (e.key === "-") setScale((s) => Math.max(1, s - 0.5));
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);

  const zoomIn = () => setScale((s) => Math.min(4, s + 0.5));
  const zoomOut = () =>
    setScale((s) => {
      const ns = Math.max(1, s - 0.5);
      if (ns === 1) setPos({ x: 0, y: 0 });
      return ns;
    });
  const reset = () => {
    setScale(1);
    setPos({ x: 0, y: 0 });
  };

  const onPointerDown = (e) => {
    moved.current = false;
    if (scale <= 1) return;
    drag.current = { active: true, x: e.clientX - pos.x, y: e.clientY - pos.y };
    e.currentTarget.setPointerCapture?.(e.pointerId);
  };
  const onPointerMove = (e) => {
    if (!drag.current.active) return;
    moved.current = true;
    setPos({ x: e.clientX - drag.current.x, y: e.clientY - drag.current.y });
  };
  const onPointerUp = () => {
    drag.current.active = false;
  };
  const onBackdropClick = (e) => {
    if (e.target === e.currentTarget && !moved.current) onClose();
  };

  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      transition={{ duration: 0.25 }}
      className="fixed inset-0 z-[90] flex flex-col bg-black/92 backdrop-blur-md"
      data-testid="deck-zoom-viewer"
      data-lenis-prevent
    >
      <div className="flex items-center justify-between px-5 py-4 sm:px-8">
        <span className="font-sans text-[11px] uppercase tracking-[0.18em] text-mds-cream/70">
          Page {String(page).padStart(2, "0")} · {Math.round(scale * 100)}%
        </span>
        <div className="flex items-center gap-2">
          <button
            onClick={zoomOut}
            disabled={scale <= 1}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-mds-cream transition-colors hover:border-mds-gold hover:text-mds-gold disabled:opacity-25"
            data-testid="deck-zoom-out"
            aria-label="Zoom out"
          >
            <ZoomOut size={18} />
          </button>
          <button
            onClick={zoomIn}
            disabled={scale >= 4}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-mds-cream transition-colors hover:border-mds-gold hover:text-mds-gold disabled:opacity-25"
            data-testid="deck-zoom-in"
            aria-label="Zoom in"
          >
            <ZoomIn size={18} />
          </button>
          <button
            onClick={reset}
            className="flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-mds-cream transition-colors hover:border-mds-gold hover:text-mds-gold"
            data-testid="deck-zoom-reset"
            aria-label="Reset zoom"
          >
            <Maximize2 size={16} />
          </button>
          <button
            onClick={onClose}
            className="ml-1 flex h-10 w-10 items-center justify-center rounded-full border border-white/25 text-mds-cream transition-colors hover:border-mds-gold hover:text-mds-gold"
            data-testid="deck-zoom-close"
            aria-label="Close zoom"
          >
            <X size={18} />
          </button>
        </div>
      </div>

      <div
        className="relative flex flex-1 items-center justify-center overflow-hidden px-4 pb-8"
        style={{ cursor: scale > 1 ? (drag.current.active ? "grabbing" : "grab") : "zoom-out" }}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerLeave={onPointerUp}
        onClick={onBackdropClick}
        data-testid="deck-zoom-backdrop"
      >
        <img
          src={hiSrc(page)}
          alt={`Sponsorship deck page ${page}, enlarged`}
          className="max-h-full max-w-full select-none rounded-sm shadow-2xl"
          draggable={false}
          onDoubleClick={() => (scale > 1 ? reset() : setScale(2))}
          style={{
            transform: `translate(${pos.x}px, ${pos.y}px) scale(${scale})`,
            transition: drag.current.active ? "none" : "transform 0.25s ease-out",
          }}
          data-testid="deck-zoom-image"
        />
      </div>
      <p className="pb-4 text-center font-sans text-[10px] uppercase tracking-[0.18em] text-mds-cream/50">
        Tap outside the page or press ✕ to close
      </p>
    </motion.div>
  );
};

export const DeckModal = ({ open, onClose }) => {
  const bookRef = useRef(null);
  const [page, setPage] = useState(0);
  const [zoomPage, setZoomPage] = useState(null);
  const pageRef = useRef(0);
  const zoomRef = useRef(null);

  useEffect(() => {
    zoomRef.current = zoomPage;
  }, [zoomPage]);

  const flipNext = useCallback(() => {
    bookRef.current?.pageFlip()?.flipNext();
  }, []);
  const flipPrev = useCallback(() => {
    bookRef.current?.pageFlip()?.flipPrev();
  }, []);
  const flipTo = useCallback((idx) => {
    bookRef.current?.pageFlip()?.flip(idx);
  }, []);

  const onFlip = useCallback((e) => {
    setPage(e.data);
    pageRef.current = e.data;
  }, []);

  useEffect(() => {
    if (!open) return;
    setPage(0);
    setZoomPage(null);
    pageRef.current = 0;
    zoomRef.current = null;
    const t1 = setTimeout(() => window.dispatchEvent(new Event("resize")), 60);
    const t2 = setTimeout(() => window.dispatchEvent(new Event("resize")), 350);

    const onKey = (e) => {
      if (zoomRef.current) return;
      if (e.key === "Escape") onClose();
      if (e.key === "ArrowRight") flipNext();
      if (e.key === "ArrowLeft") flipPrev();
    };

    // react-pageflip reparents page DOM nodes, so React click handlers don't fire.
    // Use a native capture-phase listener and map the click to the correct page.
    const onDocClick = (ev) => {
      if (zoomRef.current) return;
      const parent = document.querySelector(".deck-flipbook.stf__parent");
      if (!parent || !parent.contains(ev.target)) return;
      const rect = parent.getBoundingClientRect();
      const isLeft = ev.clientX < rect.left + rect.width / 2;
      const p = pageRef.current;
      let target;
      if (p === 0) target = 1;
      else if (p >= TOTAL - 1) target = TOTAL;
      else target = isLeft ? p + 1 : p + 2;
      setZoomPage(target);
    };

    window.addEventListener("keydown", onKey);
    document.addEventListener("click", onDocClick, true);
    document.body.style.overflow = "hidden";
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      window.removeEventListener("keydown", onKey);
      document.removeEventListener("click", onDocClick, true);
      document.body.style.overflow = "";
    };
  }, [open, flipNext, flipPrev, onClose]);

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          transition={{ duration: 0.4 }}
          className="fixed inset-0 z-[80] flex flex-col bg-mds-paper backdrop-blur-xl"
          data-testid="deck-modal"
          data-lenis-prevent
        >
          <div className="flex items-center justify-between border-b border-mds-line px-5 py-4 sm:px-8">
            <div className="flex items-center gap-3">
              <img src="/brand/mark-real.png" alt="" className="h-8 w-8 object-contain" />
              <div>
                <p className="font-display smallcaps text-lg leading-none text-mds-ink">Sponsorship Opportunities</p>
                <p className="font-sans text-[10px] uppercase tracking-[0.18em] text-mds-inksoft">
                  Million Dollar Shot Fort Myers 2026
                </p>
              </div>
            </div>
            <div className="flex items-center gap-3">
              <a
                href={LINKS.deckPdf}
                target="_blank"
                rel="noopener noreferrer"
                className="hidden items-center gap-2 rounded-full border border-mds-line px-4 py-2 font-sans text-[11px] uppercase tracking-[0.14em] text-mds-inksoft transition-colors hover:border-mds-gold hover:text-mds-gold sm:flex"
                data-testid="deck-download"
              >
                <Download size={13} /> PDF
              </a>
              <button
                onClick={onClose}
                className="flex h-10 w-10 items-center justify-center rounded-full border border-mds-line text-mds-ink transition-colors hover:border-mds-gold hover:text-mds-gold"
                data-testid="deck-close"
                aria-label="Close deck"
              >
                <X size={18} />
              </button>
            </div>
          </div>

          <div className="relative flex flex-1 items-center justify-center overflow-hidden px-4 py-6 sm:px-20">
            <button
              onClick={flipPrev}
              disabled={page === 0}
              className="absolute left-2 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-mds-line bg-mds-white/70 text-mds-ink transition-colors hover:border-mds-gold hover:text-mds-gold disabled:opacity-25 sm:left-6"
              data-testid="deck-prev"
              aria-label="Previous page"
            >
              <ChevronLeft size={22} />
            </button>

            <div
              className={`deck-flip-wrap flex h-full w-full items-center justify-center ${
                page === 0 ? "deck-at-cover" : page >= TOTAL - 1 ? "deck-at-back" : "deck-open"
              }`}
            >
              <HTMLFlipBook
                ref={bookRef}
                width={500}
                height={648}
                minWidth={280}
                maxWidth={560}
                minHeight={363}
                maxHeight={725}
                size="stretch"
                showCover={true}
                usePortrait={true}
                mobileScrollSupport={true}
                useMouseEvents={false}
                showPageCorners={false}
                disableFlipByClick={true}
                maxShadowOpacity={0.5}
                drawShadow={true}
                flippingTime={800}
                className="deck-flipbook"
                onFlip={onFlip}
                data-testid="deck-flipbook"
              >
                {Array.from({ length: TOTAL }).map((_, i) => (
                  <Page key={i} number={i + 1} hard={false} />
                ))}
              </HTMLFlipBook>
            </div>

            <button
              onClick={flipNext}
              disabled={page >= TOTAL - 1}
              className="absolute right-2 z-10 flex h-12 w-12 items-center justify-center rounded-full border border-mds-line bg-mds-white/70 text-mds-ink transition-colors hover:border-mds-gold hover:text-mds-gold disabled:opacity-25 sm:right-6"
              data-testid="deck-next"
              aria-label="Next page"
            >
              <ChevronRight size={22} />
            </button>
          </div>

          <div className="border-t border-mds-line px-5 py-4 sm:px-8">
            <div className="mx-auto flex max-w-3xl items-center justify-between">
              <span className="font-display text-lg text-mds-ink" data-testid="deck-page-indicator">
                {String(page + 1).padStart(2, "0")} <span className="text-mds-inksoft">/ {TOTAL}</span>
              </span>
              <p className="hidden font-sans text-[10px] uppercase tracking-[0.16em] text-mds-inksoft md:block">
                Tap a page to zoom
              </p>
              <div className="hidden gap-1.5 sm:flex">
                {Array.from({ length: TOTAL }).map((_, i) => (
                  <button
                    key={i}
                    onClick={() => flipTo(i)}
                    className={`h-1.5 rounded-full transition-all duration-300 ${
                      page === i ? "w-6 bg-mds-gold" : "w-1.5 bg-mds-line hover:bg-mds-goldlt"
                    }`}
                    aria-label={`Go to page ${i + 1}`}
                  />
                ))}
              </div>
            </div>
          </div>

          <AnimatePresence>
            {zoomPage && <ZoomViewer page={zoomPage} onClose={() => setZoomPage(null)} />}
          </AnimatePresence>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
